import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { getHQPartnerSettings, HQPartnerSettings } from '../services/partnerService';
import { NAV_ITEMS } from '../data/siteContent';
import { useWebsiteContent } from '../contexts/WebsiteContentContext';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [hqSettings, setHqSettings] = useState<HQPartnerSettings | null>(null);
  const location = useLocation();
  const cms = useWebsiteContent();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // The CMS nav (websiteContent/gridsAndCards.navItems) is the source of truth
  // for WHICH top-level items exist, their labels and their order — so the
  // admin's add/remove/rename/reorder all take real effect. Dropdown children
  // stay code-owned (NAV_ITEMS) and attach automatically by path match.
  // Old behaviour kept unmatched static items at the end of the bar, which
  // made deleting an item in the admin a silent no-op.
  // Ship a new top-level item by adding it to NAV_ITEMS AND running
  // scripts/sync-cms-nav.js (a saved CMS doc otherwise won't include it).
  const navItems = (() => {
    const cmsNav = cms?.navItems;
    if (!cmsNav?.length) return NAV_ITEMS;

    return [...cmsNav]
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
      .map((cmsItem) => {
        const staticMatch = NAV_ITEMS.find((s) => s.path === cmsItem.path);
        return staticMatch
          ? { ...staticMatch, label: cmsItem.label }
          : { label: cmsItem.label, path: cmsItem.path };
      }) as typeof NAV_ITEMS;
  })();
  useEffect(() => {
    const loadHQSettings = async () => {
      const settings = await getHQPartnerSettings();
      setHqSettings(settings);
    };
    loadHQSettings();
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  /** Check if any child path of a dropdown nav item is currently active */
  const isDropdownActive = (item: typeof navItems[number]) => {
    if (!item.children) return false;
    return item.children.some(child => !child.external && isActive(child.path));
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            {hqSettings?.logoUrl ? (
              <img
                src={hqSettings.logoUrl}
                alt={hqSettings.name || 'PropInvest\u2122'}
                width={160}
                height={48}
                className="h-10 md:h-12 w-auto"
                decoding="async"
              />
            ) : (
              <div className="text-2xl font-extrabold tracking-tight">
                <span className="text-gray-900">PROP</span>
                <span className="text-primary">INVEST</span>
                <span className="text-gray-400 text-xs align-super ml-0.5">&#8482;</span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
            {navItems.map((item) => {
              if (item.children) {
                // Dropdown menu item — the parent is a real crawlable link that
                // navigates; the panel opens on hover, or via the chevron button
                // (touch/keyboard). Children stay mounted in the DOM at all
                // times (CSS show/hide) so crawlers can see every child link.
                const isOpen = openDropdown === item.label;
                const dropdownColor = isDropdownActive(item)
                  ? 'text-primary'
                  : 'text-gray-700 group-hover:text-primary';
                return (
                  <div
                    key={item.label}
                    className="relative"
                    // pointerType guard: touch taps emit emulated mouseenter BEFORE
                    // click, which would pre-open the panel and make the chevron's
                    // click toggle close it again instantly. Hover-open is mouse-only.
                    onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpenDropdown(item.label); }}
                    onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpenDropdown(prev => (prev === item.label ? null : prev)); }}
                  >
                    <div className="group flex items-center rounded">
                      <Link
                        to={item.path}
                        className={`pl-3 pr-0.5 py-2 text-sm font-semibold tracking-wide transition-colors ${dropdownColor}`}
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setOpenDropdown(isOpen ? null : item.label)}
                        aria-expanded={isOpen}
                        aria-label={`Open ${item.label} menu`}
                        className={`pr-2 py-2 transition-colors ${dropdownColor}`}
                      >
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    {/* pt-1 (not mt-1) bridges the 4px visual gap so the pointer
                        never leaves the wrapper en route to the panel — with a
                        margin, onMouseLeave would close the menu mid-travel */}
                    <div className={`${isOpen ? 'block' : 'hidden'} absolute top-full left-0 pt-1 z-50`}>
                      <div className="w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1.5">
                      {item.children.map((child) => {
                          if (child.external) {
                            return (
                              <a
                                key={child.label}
                                href={child.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-light hover:text-primary transition-colors"
                                onClick={() => setOpenDropdown(null)}
                              >
                                {child.label}
                                <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                              </a>
                            );
                          }
                          return (
                            <Link
                              key={child.label}
                              to={child.path}
                              className={`block px-4 py-2.5 text-sm transition-colors ${
                                isActive(child.path)
                                  ? 'bg-primary-light text-primary font-semibold'
                                  : 'text-gray-700 hover:bg-primary-light hover:text-primary'
                              }`}
                              onClick={() => setOpenDropdown(null)}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              // Regular nav item
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 text-sm font-semibold tracking-wide transition-colors rounded ${
                    isActive(item.path)
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-primary transition"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navItems.map((item) => {
              if (item.children) {
                const isExpanded = mobileExpanded === item.label;
                return (
                  <div key={item.label}>
                    <div
                      className={`flex items-center rounded-lg text-sm font-semibold transition-colors ${
                        isDropdownActive(item)
                          ? 'bg-primary-light text-primary'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                      }`}
                    >
                      <Link to={item.path} className="flex-1 px-4 py-3">
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setMobileExpanded(isExpanded ? null : item.label)}
                        aria-expanded={isExpanded}
                        aria-label={`Open ${item.label} menu`}
                        className="px-4 py-3"
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    <div className={`${isExpanded ? 'block' : 'hidden'} ml-4 mt-1 space-y-0.5 border-l-2 border-primary/20 pl-3`}>
                      {item.children.map((child) => {
                          if (child.external) {
                            return (
                              <a
                                key={child.label}
                                href={child.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-primary transition-colors"
                              >
                                {child.label}
                                <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                              </a>
                            );
                          }
                          return (
                            <Link
                              key={child.label}
                              to={child.path}
                              className={`block px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                isActive(child.path)
                                  ? 'bg-primary-light text-primary font-semibold'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                              }`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-light text-primary'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
